import {
  CACHE_PICKUP_RADIUS_PX,
  CAMP_DEFS,
  CAMP_INITIAL_SPAWN_AT_SEC,
  CAMP_PATROL_RADIUS_PX,
  CAMP_UNIT_VISION_TILES,
  type CampUnitDef,
} from '../../data/camps.js';
import { getMap } from '../../data/maps/index.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { ControlNode } from '../entities/ControlNode.js';
import type { NeutralCamp, NeutralUnit, SignalCache } from '../entities/NeutralCamp.js';
import type { EntityId, PlayerId, Tick } from '../ids.js';
import { entityId, tickOf } from '../ids.js';

// V1.5 neutral camp lifecycle. CampSystem owns the spawn / patrol / clear /
// cache state machine. CombatSystem already does the actual gunplay
// (acquireAndFireNeutralUnits) and damage routing — this system handles
// non-combat motion (patrol), reaping cleared camps, and dropping caches.
//
// Lifecycle (per camp anchor):
//   1. createInitialState seeds an empty neutralCamps table.
//   2. CampSystem on the FIRST tick (matchSeconds >= 0) mints the tier-1 camp.
//   3. While camp.state === 'active': units patrol or chase. CampSystem reaps
//      any unit whose hp <= 0; when unitIds is empty, camp.state flips to
//      'cleared' and a SignalCache is spawned at the anchor.
//   4. The cache is picked up by the first player squad to enter
//      CACHE_PICKUP_RADIUS_PX of the cache; cache is removed; player.signal +=
//      cache.signalAmount.
//   5. After the cache is collected, the camp becomes 'depleted'. It never
//      respawns; once the green turret and drones are destroyed, the relay
//      stays permanently unguarded.

export function campSystem(state: MatchState): void {
  if (!state.enableNeutralCamps) return;
  ensureInitialSpawn(state);
  patrolUnits(state);
  reapDeadUnits(state);
  maybeClearCamp(state);
  pickupCaches(state);
  syncMidRelayGuardedFlag(state);
}

function ensureInitialSpawn(state: MatchState): void {
  const anchor = getMap(state.mapId).neutralCampAnchor;
  if (anchor === null) return;
  // Already minted? Skip.
  for (const c of Object.values(state.neutralCamps)) {
    if (c.tier === 1 || c.tier === 2) return;
  }
  if (state.matchSeconds < CAMP_INITIAL_SPAWN_AT_SEC) return;
  spawnCamp(state, 1, anchor);
}

function spawnCamp(
  state: MatchState,
  tier: 1 | 2,
  anchor: { x: number; y: number },
): NeutralCamp {
  const def = tier === 1 ? CAMP_DEFS.tier1 : CAMP_DEFS.tier2;
  const campId = mint(state);
  const unitIds: EntityId[] = [];

  // Turret — stationary at the anchor.
  const turretId = mint(state);
  state.neutralUnits[turretId] = unitFromDef(turretId, campId, 'turret', anchor, def.turret);
  unitIds.push(turretId);

  // Patrol units — N units arranged in a ring around the anchor at
  // CAMP_PATROL_RADIUS_PX, each with a 2-waypoint patrol opposite the spawn.
  for (let i = 0; i < def.patrolCount; i++) {
    const angle = (i / def.patrolCount) * Math.PI * 2;
    const startPos = {
      x: anchor.x + Math.cos(angle) * CAMP_PATROL_RADIUS_PX,
      y: anchor.y + Math.sin(angle) * CAMP_PATROL_RADIUS_PX,
    };
    const oppositePos = {
      x: anchor.x + Math.cos(angle + Math.PI) * CAMP_PATROL_RADIUS_PX,
      y: anchor.y + Math.sin(angle + Math.PI) * CAMP_PATROL_RADIUS_PX,
    };
    const id = mint(state);
    const unit = unitFromDef(id, campId, 'patrol', startPos, def.patrol);
    unit.patrolPoints = [startPos, oppositePos];
    unit.patrolIndex = 1;
    state.neutralUnits[id] = unit;
    unitIds.push(id);
  }

  const camp: NeutralCamp = {
    id: campId,
    pos: { x: anchor.x, y: anchor.y },
    tier,
    state: 'active',
    stateSinceTick: state.tick,
    unitIds,
  };
  state.neutralCamps[campId] = camp;
  return camp;
}

function unitFromDef(
  id: EntityId,
  campId: EntityId,
  kind: 'patrol' | 'turret',
  pos: { x: number; y: number },
  def: CampUnitDef,
): NeutralUnit {
  return {
    id,
    campId,
    kind,
    pos: { x: pos.x, y: pos.y },
    facing: 0,
    hp: def.hp,
    maxHp: def.hp,
    range: def.range,
    damagePerShot: def.damagePerShot,
    attackCooldownSec: def.attackCooldownSec,
    attackCooldown: 0,
    projectileSpeed: def.projectileSpeed,
    speed: def.speed,
    patrolPoints: [],
    patrolIndex: 0,
    aggroTargetId: null,
  } satisfies Omit<NeutralUnit, 'patrolPoints' | 'patrolIndex'> & {
    patrolPoints: { x: number; y: number }[];
    patrolIndex: number;
  };
}

// When an aggroed guard has its target inside firing range it stops and
// shoots instead of sprinting on top of the player squad. Same hysteresis
// (0.9) the squad halt uses in MovementSystem — keeps a small standoff so a
// jittering target doesn't toggle the guard between walk/halt every tick.
const NEUTRAL_AGGRO_HALT_RANGE_MULT = 0.9;

// Aggroed units close to firing range and then hold; idle units cycle through
// their patrol waypoints. Stationary units (turrets) don't move regardless.
// CampSystem owns motion exclusively for neutral units — they don't go
// through MovementSystem (which is owner-aware and pathfinds).
function patrolUnits(state: MatchState): void {
  for (const u of Object.values(state.neutralUnits)) {
    if (u.speed <= 0) continue;
    if (u.aggroTargetId !== null) {
      const target = state.squads[u.aggroTargetId];
      if (target !== undefined) {
        const dx = target.pos.x - u.pos.x;
        const dy = target.pos.y - u.pos.y;
        const haltRange = u.range * NEUTRAL_AGGRO_HALT_RANGE_MULT;
        if (dx * dx + dy * dy > haltRange * haltRange) {
          moveTowardPoint(u, target.pos);
        } else {
          // In range — turn to face the target but hold position so we
          // shoot from a sensible standoff instead of charging into melee.
          u.facing = Math.atan2(dy, dx);
        }
        continue;
      }
      u.aggroTargetId = null;
    }
    if (u.patrolPoints.length === 0) continue;
    const wp = u.patrolPoints[u.patrolIndex] ?? u.patrolPoints[0]!;
    const reached = moveTowardPoint(u, wp);
    if (reached) {
      u.patrolIndex = (u.patrolIndex + 1) % u.patrolPoints.length;
    }
  }
}

// Move a single SECONDS_PER_TICK step toward a point; return true once we're
// within a small tolerance of the destination.
function moveTowardPoint(unit: NeutralUnit, target: { x: number; y: number }): boolean {
  const dx = target.x - unit.pos.x;
  const dy = target.y - unit.pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= 1) return true;
  const step = Math.min(dist, unit.speed * SECONDS_PER_TICK);
  unit.pos.x += (dx / dist) * step;
  unit.pos.y += (dy / dist) * step;
  unit.facing = Math.atan2(dy, dx);
  return false;
}

function reapDeadUnits(state: MatchState): void {
  const dead: EntityId[] = [];
  for (const u of Object.values(state.neutralUnits)) {
    if (u.hp <= 0) dead.push(u.id);
  }
  if (dead.length === 0) return;
  for (const id of dead) {
    const unit = state.neutralUnits[id];
    if (unit === undefined) continue;
    const camp = state.neutralCamps[unit.campId];
    if (camp !== undefined) {
      camp.unitIds = camp.unitIds.filter((u) => u !== id);
    }
    delete state.neutralUnits[id];
  }
}

function maybeClearCamp(state: MatchState): void {
  for (const camp of Object.values(state.neutralCamps)) {
    if (camp.state !== 'active') continue;
    if (camp.unitIds.length > 0) continue;
    // Tier-spawn tick lives on `camp.stateSinceTick` until the transition
    // overwrites it. Capture it BEFORE flipping state so duration math
    // uses the spawn tick, not the clear tick.
    const spawnTick = camp.stateSinceTick;
    camp.state = 'cleared';
    camp.stateSinceTick = state.tick;
    // Telemetry — record the clear tick, who did most damage to this
    // tier's units, and how long it took. The pickup attribution (which
    // player got the cache) is tracked separately in pickupCaches because
    // cleared-by ≠ picked-by in general (a strong damage dealer can lose
    // the cache to a flanking Recon).
    const damage = camp.tier === 1
      ? state.stats.campTier1DamageByPlayer
      : state.stats.campTier2DamageByPlayer;
    const clearer = damage[0] > damage[1] ? 0 : damage[1] > damage[0] ? 1 : null;
    const durationSec = (state.tick - spawnTick) * SECONDS_PER_TICK;
    if (camp.tier === 1 && state.stats.campTier1ClearedAtTick === null) {
      state.stats.campTier1ClearedAtTick = state.tick;
      state.stats.campTier1ClearedByPlayer = clearer;
      state.stats.campTier1ClearedDurationSec = durationSec;
    } else if (camp.tier === 2 && state.stats.campTier2ClearedAtTick === null) {
      state.stats.campTier2ClearedAtTick = state.tick;
      state.stats.campTier2ClearedByPlayer = clearer;
      state.stats.campTier2ClearedDurationSec = durationSec;
    }
    spawnCacheFor(state, camp);
  }
}

function spawnCacheFor(state: MatchState, camp: NeutralCamp): void {
  const def = camp.tier === 1 ? CAMP_DEFS.tier1 : CAMP_DEFS.tier2;
  const id = mint(state);
  const cache: SignalCache = {
    id,
    campId: camp.id,
    pos: { x: camp.pos.x, y: camp.pos.y },
    signalAmount: def.cacheSignal,
    pickupRadius: CACHE_PICKUP_RADIUS_PX,
    spawnedAtTick: state.tick,
  };
  state.signalCaches[id] = cache;
}

// First player squad to step inside the cache's pickupRadius collects.
// Deterministic tiebreak: iterate caches and squads in id order. We don't
// expect contested pickups in V1.5 (the cache spawns when the camp is dead,
// so any squad nearby is the clearer), but the rule is documented anyway so
// future contests resolve the same way every time.
function pickupCaches(state: MatchState): void {
  const cacheIds = Object.keys(state.signalCaches)
    .map((k) => Number(k) as EntityId)
    .sort((a, b) => a - b);
  const collected: EntityId[] = [];
  for (const cid of cacheIds) {
    const cache = state.signalCaches[cid];
    if (cache === undefined) continue;
    const squadIds = Object.keys(state.squads)
      .map((k) => Number(k) as EntityId)
      .sort((a, b) => a - b);
    let collector: PlayerId | null = null;
    const radSq = cache.pickupRadius * cache.pickupRadius;
    for (const sid of squadIds) {
      const sq = state.squads[sid];
      if (sq === undefined) continue;
      const dx = sq.pos.x - cache.pos.x;
      const dy = sq.pos.y - cache.pos.y;
      if (dx * dx + dy * dy > radSq) continue;
      collector = sq.ownerId;
      break;
    }
    if (collector === null) continue;
    state.players[collector].signal += cache.signalAmount;
    state.stats.cachesPickedByPlayer[collector] += 1;
    collected.push(cid);
    // After the one-time cache pickup we mark the camp as depleted so HUD
    // knows there will be no further spawns.
    const camp = state.neutralCamps[cache.campId];
    if (camp !== undefined && camp.state === 'cleared') {
      camp.state = 'depleted';
      camp.stateSinceTick = state.tick;
    }
  }
  for (const id of collected) delete state.signalCaches[id];
}

// Mirror the camp lifecycle on the mid relay's `guardedBy` flag so HUD / bots
// can read a single source of truth. We only flip the flag for relays that
// sit at a known camp anchor — Foundry's mid relay is the only one in V1.5,
// but the loop is anchor-keyed so a second map can wire its own.
function syncMidRelayGuardedFlag(state: MatchState): void {
  const anchor = getMap(state.mapId).neutralCampAnchor;
  if (anchor === null) return;
  let nodeAtAnchor: ControlNode | null = null;
  for (const node of Object.values(state.controlNodes)) {
    if (node.pos.x === anchor.x && node.pos.y === anchor.y) {
      nodeAtAnchor = node;
      break;
    }
  }
  if (nodeAtAnchor === null) return;
  const camp = firstCamp(state);
  const guarded = camp !== null && camp.state === 'active';
  nodeAtAnchor.guardedBy = guarded ? 'neutral_camp' : null;
}

function firstCamp(state: MatchState): NeutralCamp | null {
  const ids = Object.keys(state.neutralCamps).map((k) => Number(k) as EntityId).sort((a, b) => a - b);
  for (const id of ids) {
    const c = state.neutralCamps[id];
    if (c !== undefined) return c;
  }
  return null;
}

function mint(state: MatchState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return entityId(id);
}

// Re-export for tests that want to hand-mint a camp at a non-default anchor.
export function spawnCampForTest(
  state: MatchState,
  tier: 1 | 2,
  anchor: { x: number; y: number },
): NeutralCamp {
  return spawnCamp(state, tier, anchor);
}

// Re-export so callers (HUD, tests) can also get the camp's vision footprint
// for fog math without hard-coding the constant.
export const NEUTRAL_UNIT_VISION_TILES = CAMP_UNIT_VISION_TILES;

// Helper for tests to advance match time-relative tick boundaries without
// reaching into Simulation internals. Re-exported here because CampSystem is
// the only consumer that cares about `Tick` arithmetic for camp scheduling.
export function tickOfMatchSeconds(seconds: number): Tick {
  return tickOf(Math.round(seconds / SECONDS_PER_TICK));
}
